import { useEffect, useRef, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';

/**
 * UserMenu — compact avatar button in the Header that opens a small
 * dropdown panel showing the signed-in user's name, role and sign-out
 * action.
 *
 * Role label resolution: if the caller holds multiple roles, the most
 * privileged one wins (admin > asset manager > helpdesk > finance > reviewer).
 * If they hold none, we show "No role" — they can still see the shell because
 * the SWA authenticated them, but every feature page will gate their actions.
 *
 * ⚠ Every role Oscar recognises must appear in that chain. A role missing from
 * it renders as "No role", which reads as "your access did not work" to the one
 * person best placed to report it.
 *
 * Behaviour:
 *   - Click avatar → toggles the popover.
 *   - Click anywhere outside → closes it.
 *   - Escape key → closes it and returns focus to the avatar.
 *   - Sign-out is a normal anchor to /.auth/logout so SWA handles the
 *     redirect — no preventDefault.
 */
export function UserMenu() {
  const {
    user, displayName, isAdmin, isItFinanceApprover, isAssetManager, isHelpdesk, isFinance, isReviewer, loading,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (loading) {
    return (
      <span className="text-white/60 text-xs sm:text-sm" aria-live="polite">
        Loading…
      </span>
    );
  }
  if (!user) return null;

  const label = displayName ?? user.userDetails;
  // ⛔ Asset manager ranks ABOVE helpdesk: it commits spend, which helpdesk
  // cannot. Omitting it here would show "No role" to somebody who holds only
  // oscar-asset-manager — the exact wrong signal on the day the role is first
  // assigned, when everyone is watching for whether it took.
  // The finance approver ranks above the asset manager: it approves the spend the
  // asset manager can only ask for. Same reason as above - omitting it would show
  // "No role" to somebody who holds only oscar-it-finance-approver.
  const roleLabel = isAdmin
    ? 'Admin'
    : isItFinanceApprover
      ? 'IT finance approver'
      : isAssetManager
        ? 'Asset manager'
        : isHelpdesk
          ? 'Helpdesk'
          : isFinance
            ? 'Finance'
            : isReviewer
              ? 'Reviewer'
              : 'No role';
  const initials = getInitials(label);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${label}`}
        className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-orange text-white font-heading font-semibold text-[13px] leading-none shadow-sm ring-1 ring-white/20 hover:ring-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 mt-2 w-64 rounded-md border border-black/10 bg-white text-darkGrey shadow-lg ring-1 ring-black/5 z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-black/5">
            <div className="font-heading font-semibold text-sm text-navy truncate" title={label}>
              {label}
            </div>
            {user.userDetails && user.userDetails !== label && (
              <div className="text-[11px] text-darkGrey/70 truncate" title={user.userDetails}>
                {user.userDetails}
              </div>
            )}
            <div className="mt-1 inline-flex items-center text-[11px] font-semibold uppercase tracking-wide text-orange">
              {roleLabel}
            </div>
          </div>
          <a
            href="/.auth/logout"
            role="menuitem"
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-darkGrey hover:bg-bg focus:bg-bg focus:outline-none"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </a>
        </div>
      )}
    </div>
  );
}

function getInitials(label: string): string {
  if (!label) return '?';
  const cleaned = label.includes('@') ? label.split('@')[0] : label;
  const parts = cleaned
    .replace(/[._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
